/**
 * eb-modal custom element, allows us to wrap any liquid-rendered content in a modal.
 * It exposes props and custom events to control the modal from any JS context. (Preact, Alpine, or Vanilla)
 */

import register from 'preact-custom-element'
import ComponentToggleSignal from './component-toggle-signal'
import Modal, { ModalPadding } from './Modal'
import { useEffect, useRef } from 'preact/hooks'
import { ComponentChildren } from 'preact'
import { effect } from '@preact/signals'

function ModalRegister({
  open,
  padding,
  children
}: {
  open?: string
  padding?: string
  children: ComponentChildren
}) {
  const controller = useRef(new ComponentToggleSignal())

  useEffect(() => {
    if (!!open === controller.current.isOpen) return
    open ? controller.current.open() : controller.current.close()
  }, [open])

  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ref.current) return

    return effect(() => {
      const isOpen = controller.current.isOpen
      if (isOpen === !!open) return

      ref.current!.dispatchEvent(
        new CustomEvent('open-change', {
          detail: { open: isOpen },
          bubbles: true
        })
      )
    })
  }, [open])

  const paddingValue = {
    SMALL: ModalPadding.SMALL,
    MEDIUM: ModalPadding.MEDIUM,
    LARGE: ModalPadding.LARGE
  }[padding?.toUpperCase() ?? '']

  return (
    <div ref={ref}>
      <Modal controller={controller.current} padding={paddingValue}>
        {children}
      </Modal>
    </div>
  )
}

register(ModalRegister, 'eb-modal', ['open'])
